import { FacetLoadingStateService } from '@common/facet/facet-loading-state.service';
import { map } from 'rxjs/operators';
import {
    Component,
    Input,
    OnInit,
    OnChanges,
} from '@angular/core';

import { ConfirmService } from '@common/confirm';
import { VocabularyService } from 'src/app/vocabularies/vocabulary.service';
import { DataContextService } from '@services/data-context.service';
import { ReasonForChangeService } from '@common/reason-for-change/reason-for-change.service';
import { OrderService } from 'src/app/orders/order.service';
import { OrderVocabService } from 'src/app/orders/order-vocab.service';
import { Lot, Order, cv_SampleType, cv_Taxon } from '@common/types';

@Component({
    selector: 'lots-table',
    templateUrl: './lots-table.component.html',
    styles: [`
        .lots-table {
            margin-top: 10px;
           }
    `]
})
export class LotsTableComponent implements OnInit, OnChanges {
    /* The name of the object's primary key database field */
    @Input() pkName: string;
    /* The value of the object's primary key */
    @Input() pkValue: number;
    /* The entity to assign */
    @Input() entity: Order;

    @Input() readonly: boolean;

    // CVs
    sampleTypes: cv_SampleType[] = [];
    taxons: cv_Taxon[] = [];

    // State
    readonly COMPONENT_LOG_TAG = 'lots-table';

    readwrite: boolean;
    lotsShown: boolean;

    constructor(
        private facetLoadingStateService: FacetLoadingStateService,
        private orderService: OrderService,
        private orderVocabService: OrderVocabService,
        private confirmService: ConfirmService,
        private vocabularyService: VocabularyService,
        private dataContext: DataContextService,
        private reasonForChangeService: ReasonForChangeService,
    ) { }

    // lifecycle
    ngOnInit(): void {
        this.initialize();
    }

    ngOnChanges(changes: any): void {
        if (changes.entity && !changes.entity.firstChange) {
            this.initialize();
        }
    }

    initialize(): Promise<void> {
        this.facetLoadingStateService.changeLoadingState(true);

        return this.getCVs().then(() => {
            this.getLotDetails();
            this.facetLoadingStateService.changeLoadingState(false);
        }).catch((error) => {
            this.facetLoadingStateService.changeLoadingState(false);
            throw error;
        });
    }

    private getCVs(): Promise<any> {

        const p1 = this.orderVocabService.sampleTypes$.pipe(map((data) => {
            this.sampleTypes = data;
        })).toPromise();
        const p2 = this.orderVocabService.taxons$.pipe(map((data) => {
            this.taxons = data;
        })).toPromise();

        return Promise.all([p1, p2]);
    }

    // Formatters for <select> input
    sampleTypeKeyFormatter = (value: cv_SampleType): number => {
        return value.C_SampleType_key;
    }
    sampleTypeFormatter = (value: cv_SampleType): string => {
        return value.SampleType;
    }

    taxonKeyFormatter = (value: cv_Taxon): number => {
        return value.C_Taxon_key;
    }
    taxonFormatter = (value: cv_Taxon): string => {
        return value.CommonName;
    }

    createLot(): Promise<Lot> {
        const newLot = this.orderService.createLot(this.pkName, this.pkValue);

        const p1 = this.vocabularyService.getCVDefault('cv_SampleTypes').then((value) => {
            newLot.cv_SampleType = value;
        });

        return Promise.all([p1]).then(() => {
            this.lotsShown = true;
            return newLot;
        });
    }

    private getLotDetails(): Promise<string | void> {
        if (this.pkName && this.pkValue > 0) {
            return this.orderService.getLots(this.pkName, this.pkValue).then(
                (items) => {
                    if (items.length > 0) {
                        this.lotsShown = true;
                    }
                });
        }

        return Promise.resolve(this.pkName);
    }

    /**
     * Delete Lot on "x" click
     */
    removeLot(lot: Lot): Promise<void> {
        if (!this.readonly) {
            return this.confirmService.confirmDelete(
                'Delete Lot',
                'Are you sure you want to delete this lot?'
            ).then(
                // confirmed
                () => {
                    this.reasonForChangeService.markModification([lot.Order]);
                    this.orderService.deleteLot(lot);
                    if (this.pkValue > 0 &&
                        lot.C_Lot_key > 0
                    ) {
                        this.dataContext.save();
                    }
                },
                // cancel
                () => { /* do nothing on cancel */ }
            );
        }
    }

    /**
     * Hide/show lots table contents
     */
    toggleLotsShown(): void {
        this.lotsShown = !this.lotsShown;
    }
}
